import { useEffect, useRef, useState } from 'react';
import { useSession } from '../../state/useSession';
import { chatStore } from '@/storage/projectStorePort';
import type { ChatMessage } from '@/llm/types';
import { editApp } from '../../api/build';
import { Button } from '../../ui';

/**
 * Chat drives refinement. The instruction stays in the box until the change succeeds:
 * losing what someone typed because the model failed is the worst possible response to
 * a failure they did not cause.
 */
export default function ChatPanel({
  projectId,
  appId,
  appName,
  goal,
  source,
  onSource,
}: {
  projectId: string;
  /** The published app's id. An edit must re-assemble under it, never a fresh one. */
  appId: string;
  appName: string;
  goal: string;
  source: string;
  onSource: (next: string) => Promise<void>;
}) {
  const { isAuthenticated } = useSession();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listEnd = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void chatStore()
      .getByProjectId(projectId)
      .then(setMessages)
      .catch(() => setMessages([]));
  }, [projectId]);

  useEffect(() => {
    listEnd.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  async function send(): Promise<void> {
    const instruction = text.trim();
    if (!instruction || busy) return;
    setError(null);
    setBusy(true);
    const now = new Date().toISOString();
    try {
      const userMessage = await chatStore().addMessage(projectId, {
        role: 'user',
        content: instruction,
        timestamp: now,
      });
      setMessages((m) => [...m, userMessage]);

      /*
       * /edit then /assemble, under the app's EXISTING id — that id is the localStorage
       * namespace baked into the published page, so a new one would strand the data of
       * everyone already using the app.
       */
      const result = await editApp({ appId, source, instruction, appName, goal });
      await onSource(result.appCode);

      const reply = await chatStore().addMessage(projectId, {
        role: 'assistant',
        content: 'Done — the preview has the change.',
        timestamp: new Date().toISOString(),
      });
      setMessages((m) => [...m, reply]);
      setText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That change could not be made.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chat-panel">
      <ol className="chat-list">
        {messages.map((m) => (
          <li key={m.id} className={`chat-msg chat-${m.role}`}>
            <span className="type-body">{m.content}</span>
          </li>
        ))}
        <div ref={listEnd} />
      </ol>

      <div className="chat-composer">
        <label className="type-caption field-label" htmlFor="instruction">
          What should I change?
        </label>
        <textarea
          id="instruction"
          className="field"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
        <Button title="Send" onPress={() => void send()} loading={busy} disabled={!text.trim()} />
      </div>
    </div>
  );
}
